/**
 * 💝 격려 보내기 모달
 * 미리 정의된 격려 메시지를 선택하여 동역자에게 보내는 모달
 */

import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Send, Heart, ChevronRight } from 'lucide-react';
import { CHEER_CATEGORIES, CHEER_MESSAGES, getMessagesByCategory } from '../constants/cheerMessages';

export default function CheerModal({ isOpen, onClose, targetUser, onSendCheer, theme = {} }) {
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [selectedMessage, setSelectedMessage] = useState(null);
    const [isSending, setIsSending] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);

    // [NEW] 드래그 상태 - TeacherListView와 동일한 방식
    const [modalPos, setModalPos] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

    // [NEW] ESC 키 핸들러 - 이벤트 전파 중단하여 격려 모달만 닫힘
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape' && isOpen) {
                e.stopPropagation();
                e.preventDefault();
                onClose();
            }
        };
        window.addEventListener('keydown', handleKeyDown, true); // capture phase
        return () => window.removeEventListener('keydown', handleKeyDown, true);
    }, [isOpen, onClose]);

    // [NEW] 드래그 핸들러 - TeacherListView와 동일
    const handleMouseDown = (e) => {
        if (e.target.closest('button')) return;
        e.preventDefault();
        setIsDragging(true);
        setDragStart({ x: e.clientX - modalPos.x, y: e.clientY - modalPos.y });
    };

    useEffect(() => {
        const handleMouseMove = (e) => {
            if (isDragging) {
                setModalPos({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y });
            }
        };

        const handleMouseUp = () => {
            setIsDragging(false);
        };

        if (isDragging) {
            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', handleMouseUp);
            document.body.style.userSelect = 'none';
        }

        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
            document.body.style.userSelect = '';
        };
    }, [isDragging, dragStart]);

    if (!isOpen) return null;

    const categories = [
        { id: 'all', name: '전체', emoji: '💝', icon: '/images/Growing_heart.png', color: 'bg-rose-500' },
        ...Object.values(CHEER_CATEGORIES)
    ];

    const messages = getMessagesByCategory(selectedCategory);

    const handleSend = async () => {
        if (!selectedMessage || !targetUser) return;

        setIsSending(true);
        try {
            await onSendCheer({
                messageId: selectedMessage.id,
                message: selectedMessage.message,
                emoji: selectedMessage.emoji,
                category: selectedMessage.category,
                recipientId: targetUser.linkedUserId || targetUser.uid || targetUser.id,
                recipientName: targetUser.name,

            });

            setShowSuccess(true);
            setTimeout(() => {
                setShowSuccess(false);
                setSelectedMessage(null);
                onClose();
            }, 1500);
        } catch (error) {
            console.error('격려 전송 오류:', error);
        } finally {
            setIsSending(false);
        }
    };

    return createPortal(
        <div className="fixed inset-0 bg-transparent flex items-center justify-center z-[9999] p-4 pointer-events-none">
            <div
                className="bg-white/95 backdrop-blur-2xl rounded-[2.5rem] shadow-2xl w-full max-w-md overflow-hidden border border-white/50 max-h-[85vh] flex flex-col pointer-events-auto"
                style={{ transform: `translate(${modalPos.x}px, ${modalPos.y}px)` }}
                onClick={e => e.stopPropagation()}
            >
                {/* 성공 오버레이 */}
                {showSuccess && (
                    <div className="absolute inset-0 bg-gradient-to-br from-rose-400 to-pink-500 z-50 flex flex-col items-center justify-center text-white animate-fade-in">
                        <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mb-4 animate-bounce-subtle">
                            <Heart size={40} className="fill-white" />
                        </div>
                        <p className="text-xl font-black">격려가 전달되었어요! 💝</p>
                        <p className="text-sm opacity-80 mt-1">{targetUser?.name}님께 따뜻한 마음이 전해졌습니다</p>
                    </div>
                )}

                {/* 헤더 - 드래그 가능 */}
                <div
                    className="bg-gradient-to-br from-rose-100 to-pink-50 px-6 py-5 relative overflow-hidden cursor-move"
                    onMouseDown={handleMouseDown}
                >
                    <div className="absolute top-[-20%] right-[-10%] w-32 h-32 rounded-full bg-white/30 blur-2xl"></div>
                    <div className="absolute bottom-[-30%] left-[-10%] w-24 h-24 rounded-full bg-rose-200/30 blur-xl"></div>

                    <div className="relative z-10 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="w-12 h-12 rounded-2xl bg-white shadow-lg flex items-center justify-center">
                                <img src="/images/Growing_heart.png" alt="" className="w-8 h-8" />
                            </div>
                            <div>
                                <h2 className="text-lg font-black text-gray-800">격려 보내기</h2>
                                <p className="text-xs text-gray-500">
                                    <span className="font-bold text-rose-600">{targetUser?.name}</span>님에게
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            onMouseDown={(e) => e.stopPropagation()}
                            className="p-2 bg-white/50 hover:bg-white rounded-xl transition text-gray-500"
                        >
                            <X size={20} />
                        </button>
                    </div>
                </div>

                {/* 카테고리 탭 */}
                <div className="px-4 py-3 border-b border-gray-100">
                    <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
                        {categories.map(cat => (
                            <button
                                key={cat.id}
                                onClick={() => setSelectedCategory(cat.id)}
                                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold whitespace-nowrap transition-all flex-shrink-0 ${selectedCategory === cat.id
                                    ? `${cat.color} text-white shadow-lg`
                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                    }`}
                            >
                                {cat.icon && (
                                    <img src={cat.icon} alt="" className="w-4 h-4" />
                                )}
                                {cat.name}
                            </button>
                        ))}
                    </div>
                </div>

                {/* 메시지 목록 */}
                <div className="max-h-[50vh] overflow-y-auto p-4 space-y-2">
                    {messages.map(msg => (
                        <button
                            key={msg.id}
                            onClick={() => setSelectedMessage(msg)}
                            className={`w-full text-left p-4 rounded-2xl transition-all duration-200 group ${selectedMessage?.id === msg.id
                                ? 'bg-rose-50 border-2 border-rose-300 shadow-lg scale-[1.02]'
                                : 'bg-gray-50 border-2 border-transparent hover:bg-gray-100 hover:border-gray-200'
                                }`}
                        >
                            <div className="flex items-center gap-3">
                                <div className={`w-10 h-10 rounded-xl flex items-center justify-center shadow-sm transition-transform group-hover:scale-110 ${selectedMessage?.id === msg.id ? 'bg-rose-100' : 'bg-white'
                                    }`}>
                                    {msg.icon ? (
                                        <img src={msg.icon} alt="" className="w-6 h-6" />
                                    ) : (
                                        <span className="text-xl">{msg.emoji}</span>
                                    )}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className={`font-bold transition-colors ${selectedMessage?.id === msg.id ? 'text-rose-600' : 'text-gray-800'
                                        }`}>
                                        {msg.message}
                                    </p>
                                    <p className="text-xs text-gray-400 mt-0.5">{msg.description}</p>
                                </div>
                                {selectedMessage?.id === msg.id && (
                                    <div className="w-6 h-6 rounded-full bg-rose-500 text-white flex items-center justify-center">
                                        <Heart size={14} className="fill-white" />
                                    </div>
                                )}
                            </div>
                        </button>
                    ))}
                </div>

                {/* 하단 버튼 */}
                <div className="p-4 border-t border-gray-100 bg-gray-50/50">
                    <button
                        onClick={handleSend}
                        disabled={!selectedMessage || isSending}
                        className={`w-full py-4 rounded-2xl font-bold text-white flex items-center justify-center gap-2 transition-all duration-300 ${selectedMessage && !isSending
                            ? 'bg-gradient-to-r from-rose-500 to-pink-500 shadow-lg shadow-rose-500/30 hover:shadow-xl hover:shadow-rose-500/40 hover:-translate-y-0.5 active:scale-95'
                            : 'bg-gray-300 cursor-not-allowed'
                            }`}
                    >
                        {isSending ? (
                            <>
                                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                전송 중...
                            </>
                        ) : (
                            <>
                                <Send size={18} />
                                격려 보내기
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
}
